import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiGrid, FiArrowLeft } from 'react-icons/fi';
import { getCollections, getProducts } from '../firebase/database';
import ProductCard from '../components/shop/ProductCard';
import SEO from '../components/common/SEO';
import { SkeletonCard } from '../components/common/Loader';
import ContactCTA from '../components/common/ContactCTA';

// ─── Fade in wrapper ──────────────────────────────────────────────────────────
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

// Default collections when DB is empty
const DEFAULT_COLLECTIONS = [
  {
    id: 'living-room',
    name: 'Living Room',
    subtitle: 'Comfort Meets Style',
    description: 'Transform your living space with our premium seating collection. Designed for relaxation and elegance.',
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1200&q=80',
    color: '#1a3a6e',
    productCategories: ['plastic-chairs', 'outdoor'],
    featured: true,
    slug: 'living-room'
  },
  {
    id: 'office',
    name: 'Office Collection',
    subtitle: 'Work Smarter, Sit Better',
    description: 'Ergonomic office chairs engineered for productivity. Spend hours at your desk without discomfort.',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&q=80',
    color: '#e63946',
    productCategories: ['office-chairs'],
    featured: true,
    slug: 'office'
  },
  {
    id: 'kids',
    name: "Kids & Baby",
    subtitle: 'Safe & Fun Seating',
    description: 'Safety-certified baby and kids furniture. Designed with your children\'s comfort and safety in mind.',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80',
    color: '#2a5298',
    productCategories: ['baby-chairs'],
    featured: true,
    slug: 'kids'
  },
  {
    id: 'dining',
    name: 'Dining Collection',
    subtitle: 'Gather Around in Style',
    description: 'Complete dining sets and individual pieces that bring families together in comfort and style.',
    image: 'https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=1200&q=80',
    color: '#0f2347',
    productCategories: ['tables', 'dining-sets'],
    slug: 'dining'
  },
  {
    id: 'outdoor',
    name: 'Outdoor & Garden',
    subtitle: 'Al Fresco Living',
    description: 'Weather-resistant outdoor furniture for patios, gardens, and balconies across Pakistan.',
    image: 'https://images.unsplash.com/photo-1464146072230-91cabc968266?w=1200&q=80',
    color: '#1a3a6e',
    productCategories: ['outdoor'],
    slug: 'outdoor'
  },
  {
    id: 'premium',
    name: 'Premium Collection',
    subtitle: 'Luxury Redefined',
    description: 'Our finest selection — when only the best will do. Premium materials, exceptional craftsmanship.',
    image: 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=1200&q=80',
    color: '#c9a84c',
    productCategories: [],
    slug: 'premium'
  }
];

// ─── All Collections Page ─────────────────────────────────────────────────────
export const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCollections().then(data => {
      setCollections(data.length > 0 ? data : DEFAULT_COLLECTIONS);
    }).catch(() => {
      setCollections(DEFAULT_COLLECTIONS);
    }).finally(() => setLoading(false));
  }, []);

  const sortedCollections = [...collections].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <SEO
        title="Collections"
        description="Browse our curated furniture collections. Living room, office, kids, dining, outdoor and premium collections."
        keywords="furniture collections pakistan, living room furniture, office furniture, baby chairs"
      />

      {/* Hero */}
      <div className="relative bg-[#0f2347] overflow-hidden min-h-[50vh] flex items-center">
        <div className="absolute inset-0 diagonal-bg opacity-40" />
        <div className="absolute right-0 top-0 w-[500px] h-[500px] bg-[#e63946]/10 rounded-full translate-x-1/3 -translate-y-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 py-16 w-full">
          <FadeIn>
            <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Curated For You</p>
            <h1 className="font-display text-5xl md:text-6xl font-black text-white leading-tight mb-4">
              Our <span className="text-[#e63946]">Collections</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl">
              Thoughtfully curated furniture collections for every room, every lifestyle, every budget.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* All Collections — single uniform 2-column grid */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4">
          <FadeIn className="mb-10">
            <h2 className="font-display text-3xl font-black text-gray-900">All Collections</h2>
          </FadeIn>
          {sortedCollections.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-6">
              {sortedCollections.map((col, i) => (
                <FadeIn key={col.id || col.slug} delay={i * 0.1}>
                  <Link to={`/collections/${col.slug || col.id}`} className="group block relative overflow-hidden" style={{ height: 360 }}>
                    <img
                      src={col.image}
                      alt={col.name}
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                      onError={e => e.target.src = 'https://via.placeholder.com/600x360?text=' + col.name}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 transition-all duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-7">
                      <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">{col.subtitle}</p>
                      <h3 className="font-display text-2xl font-black text-white mb-2">{col.name}</h3>
                      <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-2">{col.description}</p>
                      <span className="inline-flex items-center gap-2 text-white text-sm font-bold group-hover:gap-3 transition-all">
                        Explore Collection <FiArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <FiGrid className="w-12 h-12 mx-auto mb-3" />
              <p>No collections yet. Add some from the admin panel!</p>
            </div>
          )}
        </div>
      </section>

      {/* Before-footer section — same style used everywhere else on the site */}
      <ContactCTA />
    </>
  );
};

// ─── Single Collection Page ───────────────────────────────────────────────────
export const CollectionDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    const loadCollection = async () => {
      setLoading(true);
      try {
        // Try DB first
        const dbCollections = await getCollections();
        let col = dbCollections.find(c => c.slug === slug || c.id === slug);

        // Fallback to defaults
        if (!col) col = DEFAULT_COLLECTIONS.find(c => c.slug === slug || c.id === slug);

        if (!col) { navigate('/collections'); return; }
        setCollection(col);

        // Load products that actually belong to this collection
        let prods = [];
        if (col.id) {
          const allProds = await getProducts();
          prods = allProds.filter(p => p.collectionId === col.id);
        }
        // Fallback only for the hardcoded defaults (no real collectionId to match against)
        if (prods.length === 0 && col.productCategories?.length > 0) {
          const allProds = await getProducts();
          prods = allProds.filter(p => col.productCategories.includes(p.category) || col.productCategories.includes(p.collectionId));
        }
        setProducts(prods);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadCollection();
  }, [slug]);

  const sorted = [...products].sort((a, b) => {
    if (sortBy === 'price-asc') return (a.salePrice || a.price) - (b.salePrice || b.price);
    if (sortBy === 'price-desc') return (b.salePrice || b.price) - (a.salePrice || a.price);
    if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
    return (b.createdAt || 0) - (a.createdAt || 0);
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {Array(8).fill(0).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (!collection) return null;

  return (
    <>
      <SEO
        title={collection.name}
        description={collection.description}
        image={collection.image}
      />

      {/* Collection Hero */}
      <div className="relative overflow-hidden h-[50vh] md:h-[60vh] flex items-center">
        <img
          src={collection.image}
          alt={collection.name}
          className="absolute inset-0 w-full h-full object-cover"
          onError={e => e.target.src = 'https://via.placeholder.com/1200x600?text=' + collection.name}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/30" />
        <div className="relative max-w-7xl mx-auto px-4 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/collections" className="inline-flex items-center gap-2 text-white/70 text-sm mb-4 hover:text-white transition-colors">
              <FiArrowLeft className="w-4 h-4" /> All Collections
            </Link>
            <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-2">{collection.subtitle}</p>
            <h1 className="font-display text-4xl md:text-6xl font-black text-white mb-3">{collection.name}</h1>
            <p className="text-white/70 text-lg max-w-xl">{collection.description}</p>
            <p className="text-white/50 text-sm mt-3">{products.length} products</p>
          </motion.div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Sort bar */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600 text-sm font-medium">{sorted.length} products</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#1a3a6e]">
              <option value="newest">Newest</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="text-center py-20">
            <FiGrid className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-display text-2xl font-bold text-gray-800 mb-2">No Products Yet</h3>
            <p className="text-gray-500 mb-5">Products for this collection are coming soon!</p>
            <Link to="/shop" className="btn-primary inline-block">Browse All Products</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {sorted.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </>
  );
};

export default CollectionsPage;
